import { useState, useEffect, useCallback, useRef, useContext } from "react";
import { post, C, LOGO_BASE, THUMB_BASE } from "./utils";
import { ThemeCtx, Icon } from "./AppShell";
import EchoCapturePage from "./EchoCapturePage";
const openDocTab = (doc, subId, stateid=2) => {
  const base = window.location.origin + window.location.pathname;
  // Store stateid in localStorage — no need to expose in URL
  localStorage.setItem(`doc_stateid_${doc.DOCUMENTID}`, String(stateid));
  window.open(`${base}?doc=${doc.DOCUMENTID}&dt=${doc.DOCTYPEID}&sub=${subId||""}`, "_blank", "noopener,noreferrer");
};


const isImage   = (p="") => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(p);
const fileName  = (p="") => p.split("/").pop();
const stripHtml = (h="") => h ? h.replace(/<[^>]*>/g," ").replace(/\s+/g," ").trim() : "";
const getPrimaryProduct    = (a=[]) => a.find(p=>(p.PRIMARY==="1"||p.PRIMARY===1)&&String(p.BRANDID)!=="0")?.BRANDNAME||"";
const getPrimaryIndication = (a=[]) => a.find(i=>(i.PRIMARY==="1"||i.PRIMARY===1)&&String(i.INDICATIONID)!=="0")?.INDICATIONNAME||"";
const getCompanyName = c => (c&&String(c.SPONSORID)!=="0") ? c.SPONSORNAME||"" : "";
const getCompanyLogo = c => (c&&String(c.SPONSORID)!=="0"&&c.COMPANY_LOGO) ? LOGO_BASE+c.COMPANY_LOGO : null;

// ─── Fetch hashtags — checks root response AND per-doc ───────────────────────
// ─── Paginated doc loader ─────────────────────
function useEchoLoader({ user, subproject, hashtag, stateid=2 }) {
  const [docs,     setDocs]     = useState([]);
  const [hashtags, setHashtags] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [hasMore,  setHasMore]  = useState(false);
  const [error,    setError]    = useState(null);
  const loadingRef = useRef(false);
  const pageRef    = useRef(0);

  const load = useCallback(async (reset=false) => {
    if (!subproject) return;
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true); setError(null);
    const offset = reset ? 0 : pageRef.current;
    const params = {
      subprojectid: subproject.SUBPROJECTID,
      userid:       user.USERID,
      stateid,
      echo:         1,
      sort:         "latest",
      first:        offset,
      last:         20,
    };
    if (hashtag) params.search = hashtag;
    try {
      const r = await post("advance_filter", params, user.AUTH_CODE);
      if (r.STATUS==="1" && Array.isArray(r.DATA) && r.DATA.length>0) {
        const newDocs = r.DATA;
        setDocs(prev => reset ? newDocs : [...prev, ...newDocs]);
        pageRef.current = offset + newDocs.length;
        setHasMore(newDocs.length === 20);
        // extract hashtags from first load
        if (reset) {
          const all = new Set();
          if (Array.isArray(r.HASHTAGS)) r.HASHTAGS.forEach(t => all.add(t));
          newDocs.forEach(doc => (Array.isArray(doc.HASHTAGS)?doc.HASHTAGS:[]).forEach(t => all.add(t)));
          setHashtags([...all].sort());
        }
      } else {
        if (reset) { setDocs([]); setHashtags([]); }
        setHasMore(false);
      }
    } catch { setError("Failed to load."); }
    setLoading(false);
    loadingRef.current = false;
  }, [subproject?.SUBPROJECTID, user?.USERID, hashtag, stateid]);

  useEffect(() => {
    pageRef.current = 0;
    setDocs([]); setHasMore(false);
    load(true);
  }, [subproject?.SUBPROJECTID, hashtag, stateid]);

  return { docs, hashtags, loading, hasMore, error,
    loadMore: () => { if(!loadingRef.current) load(false); },
    reload:   () => { pageRef.current=0; load(true); } };
}

// ─── Shared StatRow ───────────────────────────
const StatRow = ({ fav, attach, likes, imgCount, comments }) => (
  <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
    {fav && <Icon name="star" size={11} color="#EF9F27"/>}
    {[{n:"attach",v:attach},{n:"like",v:likes},{n:"image",v:imgCount},{n:"chat",v:comments}].map(s=>(
      <div key={s.n} style={{ display:"flex",alignItems:"center",gap:"2px",fontSize:"12px",color:C.hint }}>
        <Icon name={s.n} size={11} color={C.hint}/><span>{s.v}</span>
      </div>
    ))}
  </div>
);

// ─── Echo tile — same layout as GalleryTile + hashtags ───────────────────────
function EchoTile({ doc, subId, stateid=2 }) {
  const [hovered, setHovered] = useState(false);
  const chatbot    = Array.isArray(doc.CHATBOT)     ? doc.CHATBOT     : [];
  const coList     = Array.isArray(doc.COMPANY)?doc.COMPANY.filter(c=>String(c.SPONSORID)!=="0"):[];
  const company    = coList.find(c=>c.PRIMARY===1||c.PRIMARY==="1")||coList[0]||null;
  const products   = Array.isArray(doc.PRODUCTS)    ? doc.PRODUCTS    : [];
  const indications= Array.isArray(doc.INDICATIONS) ? doc.INDICATIONS : [];
  const hashtags   = Array.isArray(doc.HASHTAGS)    ? doc.HASHTAGS    : [];
  const imgFiles   = chatbot.filter(a => isImage(a.FILEPATH));
  const thumbUrl   = imgFiles.length>0 ? THUMB_BASE+fileName(imgFiles[0].FILEPATH) : null;
  const logoUrl    = getCompanyLogo(company);
  const sponsor    = getCompanyName(company);
  const product    = getPrimaryProduct(products);
  const indication = getPrimaryIndication(indications);
  const notes      = stripHtml(doc.NOTES||"");
  const date       = doc.DISPLAY_TIME||"";
  const fav        = doc.MYFAVOURITES==="YES";

  const ctx      = useContext(ThemeCtx)||{};
  const theme    = ctx.theme||ctx||{};
  const grad     = theme.tileGrad||`linear-gradient(180deg,${C.primary} 0%,#1a6fba 100%)`;
  const tPrimary = theme.primary||C.primary;

  return (
    <div onClick={()=>openDocTab(doc,subId,stateid)} onMouseEnter={()=>setHovered(true)} onMouseLeave={()=>setHovered(false)}
      style={{ background:"#fff", borderRadius:"10px",
        border:`1px solid ${hovered?tPrimary:C.border}`,
        overflow:"hidden", display:"flex", flexDirection:"row",
        cursor:"pointer", transform:hovered?"translateY(-2px)":"none",
        transition:"all 0.15s",
        boxShadow:hovered?`0 4px 20px ${tPrimary}22`:"none" }}>

      {/* Left gradient accent bar */}
      <div style={{ width:"4px", background:grad, flexShrink:0 }}/>

      <div style={{ flex:1, display:"flex", flexDirection:"column" }}>
        <div style={{ display:"flex", alignItems:"flex-start", padding:"10px", gap:"8px" }}>
          {/* Company logo + sponsor */}
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"4px", flexShrink:0, width:"40px" }}>
            <div style={{ width:"36px", height:"36px", borderRadius:"8px", border:`1px solid ${C.border}`,
              background:C.light, display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
              {logoUrl
                ? <img src={logoUrl} alt={sponsor} style={{ width:"100%",height:"100%",objectFit:"contain" }} onError={e=>e.target.style.display="none"}/>
                : sponsor ? <span style={{ fontSize:"12px",fontWeight:"600",color:tPrimary }}>{sponsor[0]}</span>
                : <Icon name="image" size={14} color={C.border}/>}
            </div>
            {sponsor && <div style={{ fontSize:"10px",color:C.muted,textAlign:"center",lineHeight:"1.2",
              wordBreak:"break-word",maxWidth:"40px",overflow:"hidden",
              display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical" }}>{sponsor}</div>}
          </div>
          {/* Title + indication */}
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:"13px",fontWeight:"500",color:C.text,lineHeight:"1.35",marginTop:"2px",
              display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden" }}>
              {product ? <><span style={{ color:tPrimary }}>{product}:</span>{" "}{doc.SUBJECT||""}</> : (doc.SUBJECT||"Untitled")}
            </div>
            {indication && <div style={{ fontSize:"12px",color:C.muted,marginTop:"1px" }}>{indication}</div>}
          </div>
          {/* Thumbnail */}
          <div style={{ width:"60px",height:"60px",borderRadius:"6px",border:`1px solid ${C.border}`,
            background:C.light,flexShrink:0,overflow:"hidden",
            display:"flex",alignItems:"center",justifyContent:"center" }}>
            {thumbUrl
              ? <img src={thumbUrl} alt="" style={{ width:"100%",height:"100%",objectFit:"contain",padding:"2px" }} onError={e=>e.target.style.display="none"}/>
              : <Icon name="image" size={16} color={C.border}/>}
          </div>
        </div>

        {/* Notes */}
        {notes && <div style={{ padding:"0 10px 6px",fontSize:"12px",color:C.muted,lineHeight:"1.45",
          display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden" }}>{notes}</div>}

        {/* Hashtags */}
        {hashtags.length>0 && (
          <div style={{ padding:"0 10px 6px", display:"flex", flexWrap:"wrap", gap:"4px" }}>
            {hashtags.map((tag,i)=>(
              <span key={i} style={{ fontSize:"10px",fontWeight:"600",color:tPrimary,
                background:`${tPrimary}12`,borderRadius:"10px",padding:"1px 7px",
                border:`1px solid ${tPrimary}25` }}>
                {tag.replace(/^#/,"")}
              </span>
            ))}
          </div>
        )}

        {/* Footer */}
        <div style={{ display:"flex",alignItems:"center",padding:"6px 10px 8px",
          borderTop:`1px solid #EEF4FB`,marginTop:"auto" }}>
          <span style={{ fontSize:"11px",color:C.hint,flex:1 }}>{date}</span>
          <StatRow fav={fav} attach={doc.COUNT||0} likes={doc.LIKES||0} imgCount={doc.IMAGECOUNT||0} comments={doc.COMMENTCOUNT||0}/>
        </div>
      </div>
    </div>
  );
}

// ─── Hashtag filter bar — drag scrollable ─────
function HashtagBar({ hashtags, active, onChange }) {
  const ref  = useRef(null);
  const drag = useRef({ on:false, x:0, sx:0 });

  useEffect(()=>{
    const el=ref.current; if(!el) return;
    const down=e=>{ drag.current={on:true,x:e.clientX,sx:el.scrollLeft}; el.style.cursor="grabbing"; };
    const move=e=>{ if(!drag.current.on) return; el.scrollLeft=drag.current.sx-(e.clientX-drag.current.x); };
    const up  =()=>{ drag.current.on=false; el.style.cursor="grab"; };
    el.addEventListener("mousedown",down);
    el.addEventListener("mousemove",move);
    el.addEventListener("mouseup",up);
    el.addEventListener("mouseleave",up);
    return ()=>{
      el.removeEventListener("mousedown",down);
      el.removeEventListener("mousemove",move);
      el.removeEventListener("mouseup",up);
      el.removeEventListener("mouseleave",up);
    };
  });

  if (!hashtags.length) return null;

  return (
    <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"14px" }}>
      <span style={{ fontSize:"10px",fontWeight:"700",color:"#9CA3AF",
        textTransform:"uppercase",letterSpacing:"0.5px",flexShrink:0 }}>Tags</span>
      <div ref={ref} style={{ flex:1,minWidth:0,overflowX:"auto",overflowY:"hidden",
        scrollbarWidth:"none",msOverflowStyle:"none",cursor:"grab",
        WebkitOverflowScrolling:"touch",userSelect:"none" }}>
        <div style={{ display:"flex",gap:"6px",width:"max-content",padding:"2px 0" }}>
          <button onMouseDown={e=>e.stopPropagation()} onClick={()=>onChange(null)}
            style={{ padding:"3px 12px",borderRadius:"20px",flexShrink:0,
              border:`1.5px solid ${!active?"#6366F1":"#E5E7EB"}`,
              background:!active?"#EEF2FF":"#fff",
              color:!active?"#6366F1":"#6B7280",
              fontSize:"11px",fontWeight:!active?"700":"500",cursor:"pointer" }}>
            All
          </button>
          {hashtags.map(tag=>{
            const a=active===tag;
            return(
              <button key={tag} onMouseDown={e=>e.stopPropagation()} onClick={()=>onChange(a?null:tag)}
                style={{ padding:"3px 12px",borderRadius:"20px",flexShrink:0,whiteSpace:"nowrap",
                  border:`1.5px solid ${a?"#6366F1":"#E5E7EB"}`,
                  background:a?"#EEF2FF":"#fff",color:a?"#6366F1":"#6B7280",
                  fontSize:"11px",fontWeight:a?"700":"500",cursor:"pointer",transition:"all 0.15s" }}>
                {tag.replace(/^#/,"")}
              </button>
            );
          })}
        </div>
      </div>
      {active && (
        <button onClick={()=>onChange(null)}
          style={{ padding:"3px 10px",borderRadius:"20px",border:"1px solid #FECACA",
            background:"#FEF2F2",color:"#EF4444",fontSize:"11px",fontWeight:"600",
            cursor:"pointer",flexShrink:0 }}>
          ✕
        </button>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────
export default function EchoScreen({ user, subproject }) {
  const ctx    = useContext(ThemeCtx)||{};
  const color  = ctx.theme?.primary||C.primary;
  const isAdmin = subproject?.ROLE===1;
  const [activeTag,    setActiveTag]    = useState(null);
  const [showCapture,  setShowCapture]  = useState(false);
  const [stateid,      setStateid]      = useState(2); // 2=active, 1=inactive

  const { docs, hashtags, loading, hasMore, error, loadMore, reload } =
    useEchoLoader({ user, subproject, hashtag: activeTag, stateid });

  // Listen for Echo Capture button from AppShell header
  useEffect(()=>{
    const handler = ()=>setShowCapture(true);
    window.addEventListener("echo-capture", handler);
    return ()=>window.removeEventListener("echo-capture", handler);
  },[]);



  return (
    <div style={{ padding:"20px", overflowY:"auto", height:"100%", boxSizing:"border-box" }}>

      {showCapture&&(
        <EchoCapturePage
          user={user} subproject={subproject}
          onClose={()=>setShowCapture(false)}
          onUploaded={()=>{ setShowCapture(false); reload&&reload(); }}/>
      )}

      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"14px" }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:"19px",fontWeight:"500",color:C.text }}>Echo</div>
          <div style={{ fontSize:"13px",color:C.muted,marginTop:"1px" }}>
            {subproject?.SUMMARY} · {docs.length} items{activeTag?` · ${activeTag}`:""}
          </div>
        </div>
        {/* Filter — admin only */}
        {isAdmin&&(
          <div style={{display:"flex",gap:"4px",background:"#F1F5F9",
            borderRadius:"8px",padding:"3px"}}>
            {[{v:2,l:"Active"},{v:1,l:"Inactive"}].map(f=>(
              <button key={f.v} onClick={()=>setStateid(f.v)}
                style={{padding:"4px 12px",borderRadius:"6px",border:"none",
                  cursor:"pointer",fontFamily:"inherit",fontSize:"11px",fontWeight:"600",
                  background:stateid===f.v?color:"transparent",
                  color:stateid===f.v?"#fff":"#6B7280",
                  transition:"all .12s"}}>
                {f.l}
              </button>
            ))}
          </div>
        )}


      </div>

      {error && <div style={{ padding:"10px 14px",borderRadius:"8px",fontSize:"14px",
        marginBottom:"1rem",background:C.errorBg,color:C.error }}>{error}</div>}

      {/* Hashtag filter */}
      <HashtagBar hashtags={hashtags} active={activeTag} onChange={setActiveTag}/>

      {/* Empty state */}
      {docs.length===0 && !loading && (
        <div style={{ textAlign:"center",padding:"4rem 0" }}>
          <Icon name="echo" size={40} color={C.border}/>
          <div style={{ fontSize:"15px",color:C.muted,marginTop:"1rem" }}>
            {activeTag?`No results for ${activeTag}`:"No Echo content found"}
          </div>
        </div>
      )}

      {/* Same grid as Gallery */}
      <div style={{ display:"grid",
        gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",
        gap:"12px" }}>
        {docs.map(doc=><EchoTile key={doc.DOCUMENTID} doc={doc} subId={subproject?.SUBPROJECTID} stateid={stateid}/>)}
      </div>

      {loading && (
        <div style={{ display:"flex",alignItems:"center",justifyContent:"center",
          gap:"8px",padding:"2rem",color:C.muted,fontSize:"14px" }}>
          <div style={{ width:"16px",height:"16px",border:`2px solid ${C.border}`,
            borderTopColor:C.primary,borderRadius:"50%",animation:"spin 0.8s linear infinite" }}/>
          Loading...
          <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
        </div>
      )}

      {hasMore && !loading && docs.length>0 && (
        <div style={{ textAlign:"center",marginTop:"1.5rem",paddingBottom:"1rem" }}>
          <button onClick={loadMore}
            style={{ padding:"9px 28px",borderRadius:"8px",border:`1px solid ${C.border}`,
              background:"#fff",color:C.primary,fontSize:"13px",fontWeight:"600",cursor:"pointer" }}>
            Load more
          </button>
        </div>
      )}
</div>
  );
}